/**
 * publicSponsors — the ONE place the public site decides which sponsors to show
 * and in what order, so /sponsor, the homepage wall, and every PartnerSlot apply
 * exactly the rules admins set in the Sponsor Manager + Placement Control:
 *
 *   1. Active only        — the admin "Active" checkbox (inactive = hidden everywhere).
 *   2. One card per brand — duplicate docs for the same company collapse to the
 *                           canonical record (highest tier, then most recently
 *                           updated) so a stale/lower-tier copy can't win.
 *   3. Placement gates    — tier entitlement (+ per-sponsor placementsOverride) and
 *                           the admin policy in settings/sponsorPlacements
 *                           (enabled tiers + max slots per surface), via the
 *                           shared engine in config/sponsorTiers.
 *   4. Admin order        — tier rank, then admin Display Order (lower first),
 *                           then name, so ties are deterministic.
 *
 * Contract end dates are deliberately NOT used to hide sponsors: the admin form
 * states expiry only flags a renewal and does not auto-hide (uncheck Active).
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firestore';
import { loadPublicSponsors } from './adminData';
import type { Sponsor } from '../types/global';
import {
  DEFAULT_PLACEMENT_POLICY,
  PlacementPolicy,
  SponsorTier,
  Surface,
  selectForSurface,
  tierRank,
} from '../config/sponsorTiers';

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const millis = (v: unknown): number => {
  const t = v as { toMillis?: () => number; seconds?: number } | undefined;
  if (t && typeof t.toMillis === 'function') return t.toMillis();
  if (t && typeof t.seconds === 'number') return t.seconds * 1000;
  return 0;
};

/** Coerce a raw Firestore sponsor doc to the fields the public UI reads. */
function toPublicSponsor(raw: { id: string } & Record<string, unknown>): Sponsor {
  return {
    ...(raw as unknown as Sponsor),
    id: raw.id,
    name: str(raw.name),
    logo: str(raw.logo),
    website: str(raw.website),
    description: str(raw.description) || undefined,
    location: str(raw.location) || undefined,
    tier: (str(raw.tier) || 'bronze') as SponsorTier,
    featured: raw.featured === true,
    active: raw.active === true,
    displayOrder: num(raw.displayOrder),
  };
}

/** Public display order: tier rank → admin Display Order → name. */
export function comparePublicSponsors(a: Sponsor, b: Sponsor): number {
  return (
    tierRank(a.tier) - tierRank(b.tier) ||
    (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
    (a.name || '').localeCompare(b.name || '')
  );
}

/** Prefer the canonical record when a company exists as more than one doc. */
function isBetterRecord(cand: Sponsor, cur: Sponsor): boolean {
  if (cand.active !== cur.active) return cand.active;
  const rc = tierRank(cand.tier);
  const rk = tierRank(cur.tier);
  if (rc !== rk) return rc < rk;
  if (!!cand.logo !== !!cur.logo) return !!cand.logo;
  return millis(cand.updatedAt) > millis(cur.updatedAt);
}

/** Collapse duplicate docs for the same company (case/space-insensitive name). */
export function dedupePublicSponsors(list: Sponsor[]): Sponsor[] {
  const best = new Map<string, Sponsor>();
  for (const s of list) {
    const key = (s.name || '').trim().toLowerCase().replace(/\s+/g, ' ') || `id:${s.id}`;
    const cur = best.get(key);
    if (!cur || isBetterRecord(s, cur)) best.set(key, s);
  }
  return Array.from(best.values());
}

/**
 * Active, de-duplicated, admin-ordered sponsors (no surface gating yet).
 * Shares the session cache with every other public sponsor read.
 */
export async function loadActivePublicSponsors(force = false): Promise<Sponsor[]> {
  const all = await loadPublicSponsors<Record<string, unknown>>(force);
  const named = all
    .map((raw) => toPublicSponsor(raw as { id: string } & Record<string, unknown>))
    .filter((s) => s.active && s.name);
  return dedupePublicSponsors(named).sort(comparePublicSponsors);
}

/**
 * The admin Placement Control policy (settings/sponsorPlacements.surfaces).
 * A surface the saved doc doesn't mention falls back to its default row; when
 * the doc is absent or unreadable the whole default policy applies — the same
 * defaults Placement Control shows admins when nothing has been saved.
 */
// Not session-cached on purpose (one tiny doc): a Placement Control save must
// show up on the next public page view, not 20 minutes later.
export async function loadPublicPlacementPolicy(): Promise<PlacementPolicy> {
  try {
    const snap = await getDoc(doc(db, 'settings', 'sponsorPlacements'));
    const saved = snap.exists() ? (snap.data()?.surfaces as PlacementPolicy | undefined) : undefined;
    return saved ? { ...DEFAULT_PLACEMENT_POLICY, ...saved } : DEFAULT_PLACEMENT_POLICY;
  } catch {
    return DEFAULT_PLACEMENT_POLICY;
  }
}

/** Apply the three-gate engine + max slots to an already-loaded active list. */
export function selectPublicForSurface(
  sponsors: Sponsor[],
  surface: Surface,
  policy: PlacementPolicy
): Sponsor[] {
  // Input is pre-sorted with a name tie-break; selectForSurface's stable sort keeps it.
  return selectForSurface([...sponsors].sort(comparePublicSponsors), surface, policy);
}

/** Everything a surface needs: the sponsors admins have placed there, in order. */
export async function getSponsorsForSurface(surface: Surface, force = false): Promise<Sponsor[]> {
  const [sponsors, policy] = await Promise.all([
    loadActivePublicSponsors(force),
    loadPublicPlacementPolicy(),
  ]);
  return selectPublicForSurface(sponsors, surface, policy);
}
